import { api } from './client'

// ---- Types ----

export interface Organization {
  id: string
  name: string
  slug: string
  primary_vertical_slug: string | null
  subscription_tier: string
  max_users: number
  is_active: boolean
  user_count: number
  onboarded_at: string | null
  created_at: string
}

export interface OrganizationDetail extends Organization {
  active_user_count: number
  admin_count: number
  settings: Record<string, unknown>
  legacy_firm_id: string | null
  active_verticals: string[]
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
}

export interface ListOrgsParams {
  page?: number
  per_page?: number
  search?: string
  is_active?: boolean | null
  vertical_slug?: string
  subscription_tier?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

export interface CreateOrgRequest {
  name: string
  slug?: string
  vertical_slug: string
  admin_email: string
  admin_name: string
  admin_phone?: string
  admin_role_slug?: string
  subscription_tier?: string
  max_users?: number
  settings?: Record<string, unknown>
  send_welcome_email?: boolean
}

export interface UpdateOrgRequest {
  subscription_tier?: string
  max_users?: number
  settings?: Record<string, unknown>
  is_active?: boolean
}

export interface OrgCreatedResponse {
  organization: Organization
  admin_user: {
    id: string
    email: string
    name: string
    role_slug: string | null
    is_active: boolean
    created_at: string | null
  }
  welcome_email_sent: boolean
}

export interface SlugCheckResponse {
  slug: string
  available: boolean
}

export interface VerticalInfo {
  id: string
  slug: string
  display_name: string
  description: string | null
  is_active: boolean
  agent_ids: string[]
  config: Record<string, unknown>
  default_roles: Record<string, unknown>
  organization_count: number
}

// ---- API functions ----

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      searchParams.set(key, String(value))
    }
  }
  return searchParams.toString()
}

export const orgApi = {
  list: (params: ListOrgsParams = {}) =>
    api.get<PaginatedResponse<Organization>>(
      `/api/v1/admin/platform/organizations?${buildQueryString(params as Record<string, unknown>)}`
    ),

  detail: (orgId: string) =>
    api.get<OrganizationDetail>(
      `/api/v1/admin/platform/organizations/${orgId}`
    ),

  create: (data: CreateOrgRequest) =>
    api.post<OrgCreatedResponse>(
      '/api/v1/admin/platform/organizations',
      data
    ),

  update: (orgId: string, data: UpdateOrgRequest) =>
    api.patch<OrganizationDetail>(
      `/api/v1/admin/platform/organizations/${orgId}`,
      data
    ),

  checkSlug: (slug: string) =>
    api.get<SlugCheckResponse>(
      `/api/v1/admin/platform/organizations/check-slug/${slug}`
    ),

  listVerticals: () =>
    api.get<VerticalInfo[]>('/api/v1/admin/platform/verticals'),
}
